// frontend/src/api/discover.ts
//
// Transport-Schicht des Entdecken-Tabs (Paket W4-C, Karte E §d–f). Der
// Server-Vertrag stammt aus Paket W4-A:
//
//   GET  /api/discover/characters?usk&setting&kind&q&lang&cursor&limit
//        → 200 { items: DiscoverItem[], next_cursor: string|null }
//   GET  /api/discover/characters/:id
//        → 200 { …voller Bogen + publication: { usk, creator, tagline, highlights } }
//   POST /api/discover/characters/:id/adopt
//        → 201 { id, chat_id }
//
// WICHTIG (Karte E §d): Entdecken-Daten laufen NUR ueber react-query — nie in
// Dexie/den Offline-Cache. Fremde Nutzerinhalte haben im lokalen Bestand eines
// anderen Kontos nichts zu suchen (Datenschutz, §f).
//
// Die USK-Stufe erzwingt der SERVER (`min(requested, ageLevel)`); der
// `usk`-Parameter darf nur verschaerfen. Der Client filtert nicht nach.

import { ApiError, apiRequest } from './client';

// R2-10: age_18 ist seit Runde 2 moeglich — aber ausschliesslich fuer
// Lorath-Vorlagen (origin='katalog'). Der Server entscheidet das, der Client
// bildet die Stufe nur ab.
export type DiscoverUsk = 'age_0' | 'age_6' | 'age_12' | 'age_16' | 'age_18';

/** Die fuenf Stufen in aufsteigender Reihenfolge (Filterleiste + Sperrlogik). */
export const DISCOVER_USK_LEVELS = ['age_0', 'age_6', 'age_12', 'age_16', 'age_18'] as const;
export type DiscoverKind = 'companion' | 'adventure';
export type DiscoverOrigin = 'katalog' | 'nutzer';

/** Die sieben Settings der Publikationen (Karte E §a, CHECK-Werte der Spalte). */
export const DISCOVER_SETTINGS = [
  'alltag',
  'fantasy',
  'scifi',
  'postapokalypse',
  'cyberpunk',
  'historisch',
  'krimi',
] as const;
export type DiscoverSetting = (typeof DISCOVER_SETTINGS)[number];

export interface DiscoverPortrait {
  url: string;
  sort: number;
}

export interface DiscoverHighlight {
  /** Lokalisierte Notiz zum Status-Bild (Server liefert EN-Fallback). */
  note: string;
  /** Tage seit dem Einzug — die Leiste sortiert danach. */
  day_offset: number | null;
  url: string;
}

export interface DiscoverCreator {
  /** Frei gewaehltes, moderiertes Label — NIE der Benutzername (Karte E §f.2). */
  label: string;
  /**
   * M6: Tier des Eigentuemers (fuer Badge-Anzeige). Server liefert
   * 'plus' | 'ultra' | 'founders' | 'torchbearer' | null.
   */
  member_badge?: import('./membership').BadgeTier | null;
}

export interface DiscoverItem {
  id: number;
  name: string;
  tagline: string;
  /** Serverwert; unbekannte Stufen bleiben als roher Text erhalten. */
  usk: string;
  kind: string;
  setting: string;
  portraits: DiscoverPortrait[];
  highlights: DiscoverHighlight[];
  creator: DiscoverCreator | null;
  published_at: string | null;
  adopted_count?: number | null;
  origin?: string;
  /**
   * „Spicy"-Kennzeichen. Der Server setzt es NUR an age_18-Katalogware UND nur
   * fuer Leser auf selected_age_level = age_18 — der Client zeigt es einfach
   * an, ohne selbst zu pruefen.
   */
  spicy?: boolean;
  [key: string]: unknown;
}

export interface DiscoverListResponse {
  items: DiscoverItem[];
  next_cursor: string | null;
  /**
   * R2-FixN: die Sprachen, in denen es in „Entdecken" ueberhaupt
   * Publikationen gibt (`character_publications.lang`, ohne 'multi'). Der
   * Server liefert das Feld seit R2-FixN bei JEDER Seite; `null` heisst
   * „unbekannt" (aelterer Server) — dann bleibt die volle Sprachliste stehen.
   * Eine LEERE Liste ist eine echte Aussage: es gibt keine fremdsprachige
   * Nutzer-Publikation.
   */
  available_langs: string[] | null;
}

/** Der Publikationsteil der Detailantwort (der Rest ist der volle Bogen). */
export interface DiscoverDetailPublication {
  usk: string;
  creator: DiscoverCreator | null;
  tagline: string;
  highlights: DiscoverHighlight[];
  portraits?: DiscoverPortrait[];
  setting?: string;
  kind?: string;
  origin?: string;
  adopted_count?: number | null;
  spicy?: boolean;
  [key: string]: unknown;
}

export interface DiscoverDetail {
  id: number;
  name: string;
  publication: DiscoverDetailPublication | null;
  /** Voller Charakterbogen — Felder wie bei `Character` (SELECT *). */
  [key: string]: unknown;
}

export interface DiscoverAdoptResponse {
  /** Die neue EIGENE Charakter-Id (Ziel der Navigation). */
  id: number;
  /** Der frisch angelegte Chat mit der Begruessung. */
  chat_id?: number | null;
}

export interface DiscoverListFilter {
  setting?: string | null;
  kind?: DiscoverKind | null;
  /** Suchbegriff (bereits entprellt). */
  q?: string | null;
  /** Darf die serverseitige Stufe nur VERSCHAERFEN („bis Stufe X"). */
  usk?: string | null;
  /**
   * Erstellersprache der NUTZER-Publikationen (R2-11 legt die Spalte an).
   * Lorath-Vorlagen liegen in allen Sprachen vor und fallen nie heraus;
   * fehlt die Spalte noch, ist der Parameter serverseitig wirkungslos.
   */
  lang?: string | null;
  limit?: number;
}

function appendParam(params: URLSearchParams, key: string, value: unknown): void {
  if (value === null || value === undefined) return;
  const text = String(value).trim();
  if (!text) return;
  params.set(key, text);
}

/** Baut den Query-String der Liste — exportiert, damit Tests ihn pruefen koennen. */
export function buildDiscoverQuery(filter: DiscoverListFilter, cursor?: string | null): string {
  const params = new URLSearchParams();
  appendParam(params, 'setting', filter.setting);
  appendParam(params, 'kind', filter.kind);
  appendParam(params, 'q', filter.q);
  appendParam(params, 'usk', filter.usk);
  appendParam(params, 'lang', filter.lang);
  appendParam(params, 'limit', filter.limit);
  appendParam(params, 'cursor', cursor);
  const text = params.toString();
  return text ? `?${text}` : '';
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function normalizeBadgeTier(value: unknown): import('./membership').BadgeTier | null {
  return ['plus', 'ultra', 'founders', 'torchbearer'].includes(String(value))
    ? value as import('./membership').BadgeTier
    : null;
}

/** Defensive Normalisierung: der Server darf Felder weglassen, die UI nicht brechen. */
export function normalizeDiscoverItem(raw: unknown): DiscoverItem | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const row = raw as Record<string, unknown>;
  const id = Number(row.id);
  if (!Number.isFinite(id)) return null;
  const creatorRaw = row.creator;
  const creator = typeof creatorRaw === 'object' && creatorRaw !== null
    ? {
      label: String((creatorRaw as { label?: unknown }).label ?? ''),
      member_badge: normalizeBadgeTier((creatorRaw as { member_badge?: unknown }).member_badge),
    }
    : null;
  return {
    ...row,
    id,
    name: typeof row.name === 'string' ? row.name : '',
    tagline: typeof row.tagline === 'string' ? row.tagline : '',
    usk: typeof row.usk === 'string' ? row.usk : '',
    kind: typeof row.kind === 'string' ? row.kind : '',
    setting: typeof row.setting === 'string' ? row.setting : '',
    portraits: asArray<DiscoverPortrait>(row.portraits).filter((p) => typeof p?.url === 'string' && p.url),
    highlights: asArray<DiscoverHighlight>(row.highlights).filter((h) => typeof h?.url === 'string' && h.url),
    creator,
    published_at: typeof row.published_at === 'string' ? row.published_at : null,
    spicy: row.spicy === true,
  };
}

export async function listDiscoverCharacters(
  filter: DiscoverListFilter,
  cursor?: string | null,
): Promise<DiscoverListResponse> {
  const response = await apiRequest<Partial<DiscoverListResponse>>(
    `/api/discover/characters${buildDiscoverQuery(filter, cursor)}`,
  );
  const items = asArray<unknown>(response?.items)
    .map(normalizeDiscoverItem)
    .filter((row): row is DiscoverItem => row !== null);
  const next = response?.next_cursor;
  // Fehlt das Feld ganz, bleibt es `null` — der Client unterscheidet damit
  // „Server kennt die Angabe nicht" von „es gibt keine".
  const rawLangs = (response as { available_langs?: unknown } | undefined)?.available_langs;
  const available_langs = Array.isArray(rawLangs)
    ? [...new Set(rawLangs.filter((code): code is string => typeof code === 'string' && !!code))]
    : null;
  return { items, next_cursor: typeof next === 'string' && next ? next : null, available_langs };
}

export function getDiscoverCharacter(id: number | string): Promise<DiscoverDetail> {
  return apiRequest<DiscoverDetail>(`/api/discover/characters/${encodeURIComponent(String(id))}`);
}

export function adoptDiscoverCharacter(id: number | string): Promise<DiscoverAdoptResponse> {
  return apiRequest<DiscoverAdoptResponse>(
    `/api/discover/characters/${encodeURIComponent(String(id))}/adopt`,
    { method: 'POST' },
  );
}

// ---------------------------------------------------------------------------
// Melden — bestehende Report-API, NEUE Surface 'publication' (Vertrag W4-A):
//   POST /api/moderation-reports { surface:'publication',
//                                  publication_character_id, reason, note? }
// Die Gruende sind bewusst eine kleine, geschlossene Liste (Server-Whitelist);
// jeder Wert hat einen i18n-Key `discover.report_reason_<wert>`.
// ---------------------------------------------------------------------------
export const PUBLICATION_REPORT_REASONS = [
  'real_person',
  'sexual_content',
  'violence',
  'hate',
  'illegal',
  'other',
] as const;
export type PublicationReportReason = (typeof PUBLICATION_REPORT_REASONS)[number];

export interface PublicationReportResponse {
  status?: 'started' | 'exists';
  [key: string]: unknown;
}

export function reportPublication(input: {
  characterId: number | string;
  reason: PublicationReportReason;
  note?: string;
}): Promise<PublicationReportResponse> {
  const note = input.note?.trim();
  return apiRequest<PublicationReportResponse>('/api/moderation-reports', {
    method: 'POST',
    body: {
      surface: 'publication',
      publication_character_id: Number(input.characterId),
      reason: input.reason,
      ...(note ? { note } : {}),
    },
  });
}

/**
 * Server-Fehlercode → i18n-Key fuer den Toast.
 *
 * W8-C (MINOR 6): Beide Aktionen der Entdecken-Flaeche teilen sich diese
 * Uebersetzung, benutzen dieselben Status-Codes aber fuer VERSCHIEDENE Dinge.
 * Ohne Kontext meldete „Uebernehmen" bei der eigenen Figur (409
 * `own_publication`, server/discover.js) den Melde-Text „Diese Figur hast du
 * bereits gemeldet." — und 403 `usk_blocked` fiel auf den allgemeinen Fehler.
 * Deshalb sagt der Aufrufer jetzt, worum es geht.
 */
export type DiscoverErrorContext = 'adopt' | 'report';

export function discoverErrorKey(
  error: unknown,
  fallbackKey: string,
  context: DiscoverErrorContext = 'report',
): string {
  if (error instanceof ApiError) {
    const code = typeof error.data === 'object' && error.data !== null
      ? (error.data as { error?: unknown }).error
      : undefined;
    if (context === 'adopt') {
      if (error.status === 409 || code === 'own_publication') return 'discover.adopt_own';
      if (error.status === 403 || code === 'usk_blocked') return 'discover.adopt_usk_blocked';
    }
    if (error.status === 429 || code === 'rate_limited') return 'discover.report_limit';
    if (error.status === 409 || code === 'already_reported') return 'discover.report_exists';
    if (error.status === 402 || code === 'insufficient_coins') return 'coins.insufficient';
    if (error.status === 404) return 'discover.not_found_body';
  }
  return fallbackKey;
}
